import React, { useState } from 'react';
import buttonStyles from '../components/Buttons.module.css';
import './foodToAvoid.css';
import pawImg from '../assets/paw.png';


const toxicFoodsByCategory = [
  {
    category: "Toxic Fruits",
    foods: [
      { name: "Grapes & Raisins", icon: "🍇", danger: "Can lead to kidney failure; even small amounts can be toxic.", level: "Severe", details: "Symptoms: vomiting, diarrhea, lethargy, loss of appetite, abdominal pain, kidney failure.\nWhat to do: Contact your vet immediately. Inducing vomiting may be recommended if ingestion was recent.", searchTerms: ["grapes", "raisins", "grape", "raisin", "sultanas", "currants"] },
      { name: "Cherries (pits)", icon: "🍒", danger: "Pits contain cyanide, which is toxic to dogs.", level: "Severe", details: "Symptoms: dilated pupils, difficulty breathing, red gums, shock.\nWhat to do: Seek emergency veterinary care if you suspect ingestion of multiple pits.", searchTerms: ["cherries", "cherry", "pits", "pit", "stones", "cherry pits"] },
      { name: "Apple Seeds", icon: "🍏", danger: "Contain cyanide, which can be toxic if consumed in large amounts.", level: "Moderate", details: "Symptoms: difficulty breathing, bright red gums, shock (rare, only if many seeds are eaten).\nWhat to do: Monitor for symptoms; call your vet if a large quantity was ingested.", searchTerms: ["apple", "apples", "seeds", "apple seeds", "cores", "apple cores"] },
      { name: "Avocado", icon: "🥑", danger: "Contains persin, which can cause vomiting and diarrhea.", level: "Moderate", details: "Symptoms: vomiting, diarrhea, abdominal pain.\nWhat to do: Mild cases may resolve on their own, but call your vet if symptoms persist.", searchTerms: ["avocado", "avocados", "guacamole", "persin"] },
    ]
  },
  {
    category: "Human Foods",
    foods: [
      { name: "Chocolate", icon: "🍫", danger: "Contains theobromine, which can cause vomiting, diarrhea, seizures, and even death.", level: "Severe", details: "Symptoms: vomiting, diarrhea, rapid breathing, increased heart rate, seizures.\nWhat to do: Call your vet or animal poison control immediately.", searchTerms: ["chocolate", "cocoa", "cacao", "theobromine", "dark chocolate", "milk chocolate", "white chocolate", "chocolates"] },
      { name: "Onions, Garlic, Chives", icon: "🧄", danger: "Contain compounds that can cause anemia and damage red blood cells.", level: "Severe", details: "Symptoms: weakness, vomiting, breathlessness, pale gums.\nWhat to do: Contact your vet; bloodwork may be needed.", searchTerms: ["onions", "onion", "garlic", "chives", "leeks", "scallions", "shallots", "green onions", "spring onions"] },
      { name: "Macadamia Nuts", icon: "🥜", danger: "Can cause weakness, tremors, and hyperthermia.", level: "Moderate", details: "Symptoms: weakness, vomiting, tremors, fever.\nWhat to do: Call your vet; most dogs recover with supportive care.", searchTerms: ["macadamia", "macadamia nuts", "macadamias", "nuts"] },
      { name: "Bread Dough", icon: "🍞", danger: "Can expand in the stomach, causing bloating and potential twisting, leading to emergency surgery.", level: "Moderate", details: "Symptoms: bloating, unproductive retching, abdominal pain.\nWhat to do: Seek emergency veterinary care immediately.", searchTerms: ["bread dough", "dough", "yeast dough", "raw dough", "unbaked dough", "pizza dough"] },
      { name: "Corn on the Cob", icon: "🌽", danger: "The cob can cause intestinal blockage.", level: "Moderate", details: "Symptoms: vomiting, loss of appetite, abdominal pain, constipation.\nWhat to do: Call your vet if you suspect ingestion.", searchTerms: ["corn", "cob", "corn cob", "corn on the cob", "corncob"] },
      { name: "Salt", icon: "🧂", danger: "Excessive salt can lead to sodium ion poisoning, causing vomiting, diarrhea, tremors, and seizures.", level: "Moderate", details: "Symptoms: vomiting, diarrhea, tremors, seizures.\nWhat to do: Contact your vet immediately.", searchTerms: ["salt", "sodium", "salty", "sea salt", "table salt", "rock salt"] },
      { name: "Xylitol", icon: "🍬", danger: "Found in sugar-free gum and candies; can cause insulin release leading to hypoglycemia and liver failure.", level: "Severe", details: "Symptoms: vomiting, weakness, collapse, seizures.\nWhat to do: Seek emergency veterinary care immediately.", searchTerms: ["xylitol", "sugar-free", "sugar free", "gum", "sweetener", "artificial sweetener"] },
    ]
  },
  {
    category: "Drinks & Stimulants",
    foods: [
      { name: "Alcohol", icon: "🍺", danger: "Can lead to vomiting, diarrhea, central nervous system depression, and respiratory failure.", level: "Severe", details: "Symptoms: vomiting, disorientation, tremors, difficulty breathing.\nWhat to do: Seek emergency veterinary care immediately.", searchTerms: ["alcohol", "beer", "wine", "liquor", "spirits", "ethanol", "alcoholic"] },
      { name: "Caffeine", icon: "☕", danger: "Found in coffee, tea, and some sodas; can cause restlessness, rapid breathing, heart palpitations, and muscle tremors.", level: "Moderate", details: "Symptoms: restlessness, rapid breathing, heart palpitations, muscle tremors.\nWhat to do: Call your vet for advice.", searchTerms: ["caffeine", "coffee", "tea", "soda", "energy drink", "caffeinated"] },
    ]
  },
  {
    category: "Other Substances",
    foods: [
      { name: "Tobacco", icon: "🚬", danger: "Contains nicotine, which can cause vomiting, drooling, and even death.", level: "Severe", details: "Symptoms: vomiting, drooling, tremors, seizures, death.\nWhat to do: Seek emergency veterinary care immediately.", searchTerms: ["tobacco", "nicotine", "cigarettes", "cigarette", "cigars", "cigar", "chewing tobacco", "vape", "vaping"] },
    ]
  },
];

// Flatten and sort foods by severity
const allFoods = toxicFoodsByCategory.flatMap(cat => cat.foods.map(food => ({ ...food, category: cat.category })));
const severityOrder: Record<string, number> = { 'Severe': 0, 'Moderate': 1 };
const getOrder = (level: string | undefined) => {
  if (!level) return 2;
  const normalized = level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
  return severityOrder[normalized] ?? 2;
};
const sortedFoods = allFoods.slice().sort((a, b) => getOrder(a.level) - getOrder(b.level));

const ToxicFoods: React.FC = () => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };
  const filteredFoods = search
    ? sortedFoods.filter(food => {
        const searchLower = search.toLowerCase();
        // Check if name matches
        if (food.name.toLowerCase().includes(searchLower)) return true;
        // Check if any search terms match
        if (food.searchTerms && food.searchTerms.some(term => term.toLowerCase().includes(searchLower))) return true;
        return false;
      })
    : sortedFoods;
  return (
    <div className="page-container cream-bg">
      {/* Floating Paw Print Background */}
      <div className="hero-paw-bg">
        <img src={pawImg} className="hero-paw-float paw1" alt="" />
        <img src={pawImg} className="hero-paw-float paw2" alt="" />
        <img src={pawImg} className="hero-paw-float paw3" alt="" />
        <img src={pawImg} className="hero-paw-float paw4" alt="" />
        <img src={pawImg} className="hero-paw-float paw5" alt="" />
        <img src={pawImg} className="hero-paw-float paw6" alt="" />
        <img src={pawImg} className="hero-paw-float paw7" alt="" />
      </div>
      <div className="page-intro">
        <h1 className="page-title">Foods Toxic to Dogs</h1>
        <div className="page-intro-underline" />
        <p className="page-intro-desc">Keep these foods away from your furry friends!</p>
        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className="search-bar-responsive"
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '1.2rem auto 1.5rem auto',
            maxWidth: 400,
          }}
        >
          <input
            type="text"
            placeholder="Search for a food..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{
              flex: '1 1 100%',
              minWidth: 0,
              padding: '0.6em 1em',
              borderRadius: '8px',
              border: '1.5px solid #ffd1ba',
              fontSize: '1rem',
              background: '#fff',
              color: '#b24646',
              outline: 'none',
              boxShadow: '0 1px 4px rgba(255,152,0,0.07)',
            }}
            aria-label="Search toxic foods"
          />
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: '0.5rem', marginTop: 8 }}>
            <button
              type="submit"
              className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
              style={{ flex: 1, minWidth: 120 }}
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                style={{ flex: 1, minWidth: 120 }}
                onClick={() => { setSearch(''); setSearchInput(''); }}
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>
      <div className="foods-container">
        {filteredFoods.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#b24646', fontWeight: 600, fontSize: '1.1rem', margin: '2.5rem 0' }}>
            No results found.
          </div>
        ) : (
          filteredFoods.map((food, i) => {
            let badge, badgeClass;
            if (food.level === 'Severe') {
              badge = '⚠️ Severe';
              badgeClass = 'danger-badge severe';
            } else if (food.level === 'Moderate') {
              badge = '⚠️ Moderate';
              badgeClass = 'danger-badge moderate';
            } else {
              badge = '🚫 Avoid';
              badgeClass = 'danger-badge avoid';
            }
            const key = food.category + food.name;
            const isExpanded = expanded === key;

            return (
              <div
                key={key}
                className={`food-card ${food.level ? food.level.toLowerCase() : ''} ${isExpanded ? 'expanded' : ''}`}
                tabIndex={0}
                role="button"
                aria-expanded={isExpanded}
                onClick={() => setExpanded(isExpanded ? null : key)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setExpanded(isExpanded ? null : key);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="food-icon" aria-label={food.name} title={food.name}>{food.icon}</div>
                <div className={badgeClass} style={{ marginBottom: '0.3rem' }}>{badge}</div>
                <h3>{food.name}</h3>
                <p>{food.danger}</p>

                {food.details && (
                  <div className={`food-details ${isExpanded ? 'open' : ''}`}>
                    {food.details.split('\n').map((line, idx) => {
                      const bolded = line.replace(/^(Symptoms:|What to do:)/, match => `<strong>${match}</strong>`);
                      return <div key={idx} dangerouslySetInnerHTML={{ __html: bolded }} />;
                    })}

                    <button
                      type="button"
                      className={buttonStyles.textLinkBtn}
                      style={{ fontSize: '0.98em', marginTop: '0.5em', padding: 0 }}
                      aria-label="Collapse details"
                      onClick={e => { e.stopPropagation(); setExpanded(null); }}
                    >
                      Click to collapse
                    </button>
                  </div>
                )}

                {!isExpanded && (
                  <button
                    type="button"
                    className={buttonStyles.textLinkBtn}
                    style={{ fontSize: '0.98em', marginTop: '0.2em', padding: 0 }}
                    tabIndex={-1}
                    aria-label="Expand for more info"
                    onClick={e => { e.stopPropagation(); setExpanded(key); }}
                  >
                    Click for more info
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ToxicFoods;